/**
 * CentralDashboardPreview — the PROPOSED single central dashboard, built from
 * the user's chosen mix, as an interactive click-through preview (sample data,
 * no auth, no backend). Route: /central-dashboard-preview
 *
 * Composite:
 *  - Active / Completed projects toggle
 *  - "Your next step" hero (Next Best Action)
 *  - "Getting started" setup tracker
 *  - Portfolio highlight card
 *  - "Start a project" opens the branching wizard:
 *      Q1 already started? -> Migration flow
 *      else Q2 funded? / contractors? -> Project Hub path OR jump to setup
 *  - Persistent "Import an existing project" secondary entry
 */
import React, { useState } from 'react';
import {
  Plus, ArrowRight, ArrowLeft, X, Activity, Shield, Target, Building2, ChevronRight,
  CheckCircle2, Circle, Clock, AlertTriangle, FileText, Users, Sparkles, Rocket,
  ListChecks, DollarSign, Table2, ScanLine, Link2, RefreshCw, ArrowRightLeft, Loader2,
  Bell, Wallet,
} from 'lucide-react';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

const ACTIVE = [
  { id: 1, name: 'Maple Ridge Townhomes', addr: 'Cedar Rapids, IA', drawn: 41, loan: 343000, gaps: 0 },
  { id: 2, name: 'Oakwood Infill', addr: 'Iowa City, IA', drawn: 12, loan: 210000, gaps: 2 },
  { id: 3, name: 'Ellis Row (draft)', addr: '204 Ellis Blvd', drawn: 0, loan: 0, gaps: 0, setup: true },
];
const COMPLETED = [
  { id: 9, name: 'Cedar Court Duplex', addr: 'Marion, IA', drawn: 100, loan: 268000 },
  { id: 10, name: 'Birchwood Flats', addr: 'Hiawatha, IA', drawn: 100, loan: 412000 },
];

// ---------------------------------------------------------------- Migration UI
const MigrationFlow = () => {
  const methods = [
    { icon: Table2, t: 'Upload a spreadsheet', s: 'Contractors, budget lines or payments-to-date. We map the columns.', tag: 'Fastest', tone: 'blue' },
    { icon: ScanLine, t: 'Drop in invoices & receipts', s: 'AI reads vendor, amount, category & date, files each to a phase.', tag: 'AI', tone: 'green' },
    { icon: Link2, t: 'Connect QuickBooks', s: 'Pull vendors, bills & payments from your books.', tag: 'Soon', tone: 'slate' },
    { icon: Plus, t: 'Enter manually', s: 'Add contractors & paid amounts by hand.', tag: null, tone: 'slate' },
  ];
  const tones = { blue: 'bg-blue-50 text-blue-700', green: 'bg-emerald-50 text-emerald-700', slate: 'bg-slate-100 text-slate-600' };
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {methods.map((m) => (
          <div key={m.t} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-sm transition cursor-pointer">
            <div className="flex items-start justify-between">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[m.tone]}`}><m.icon className="w-5 h-5" /></div>
              {m.tag && <span className="text-[10px] font-bold uppercase tracking-wide text-white px-2 py-0.5 rounded-full" style={{ background: m.tag === 'AI' ? GREEN : m.tag === 'Fastest' ? '#2563eb' : '#94a3b8' }}>{m.tag}</span>}
            </div>
            <p className="text-sm font-bold text-slate-900 mt-3">{m.t}</p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{m.s}</p>
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-bold text-slate-700 mb-2 flex items-center gap-2"><RefreshCw className="w-4 h-4 text-slate-400" /> Catch up to today</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          {[['Current draw #', '#4'], ['Drawn to date', '$141K'], ['Progress', '41%']].map(([l, v]) => (
            <div key={l} className="rounded-xl bg-white border border-slate-200 py-2"><p className="text-base font-bold text-slate-900">{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-3 flex gap-2 text-xs text-emerald-900">
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
        <span><b>Reconciled.</b> $141K imported across Framing, Foundation & Plumbing — matches 41% drawn.</span>
      </div>
    </div>
  );
};

// ------------------------------------------------------ Start-project wizard
const WizHeader = ({ title, back, setStep, onClose }) => (
  <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
    {back && <button onClick={() => setStep(back)} className="text-slate-400 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></button>}
    <h3 className="text-base font-bold text-slate-900 flex-1">{title}</h3>
    <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
  </div>
);

export const StartProjectWizard = ({ onClose }) => {
  const [step, setStep] = useState('q1');
  const [funded, setFunded] = useState(null);
  const [hasContractors, setHasContractors] = useState(null);

  const hubSteps = [
    [FileText, 'Send bid requests'], [Users, 'Compare bids'], [CheckCircle2, 'Award'],
    [Table2, 'Import to bid sheet'], [Building2, 'Send to bank'], [DollarSign, 'Mark funded'], [Rocket, 'Convert'],
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4" onClick={onClose} data-testid="start-wizard-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="start-wizard">
        {step === 'q1' && (
          <>
            <WizHeader title="Start a project" setStep={setStep} onClose={onClose} />
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">Is this project already underway, or brand new?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => setStep('migrate')} data-testid="wiz-already-started" className="text-left rounded-2xl border-2 border-slate-200 p-5 hover:border-emerald-400 hover:bg-emerald-50 transition">
                  <ArrowRightLeft className="w-6 h-6" style={{ color: GREEN }} />
                  <p className="font-bold text-slate-900 mt-3">It&apos;s already started</p>
                  <p className="text-xs text-slate-500 mt-1">Import my contractors, payments & invoices and catch up to current.</p>
                </button>
                <button onClick={() => setStep('q2')} data-testid="wiz-new" className="text-left rounded-2xl border-2 border-slate-200 p-5 hover:border-blue-400 hover:bg-blue-50 transition">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                  <p className="font-bold text-slate-900 mt-3">It&apos;s brand new</p>
                  <p className="text-xs text-slate-500 mt-1">Set it up from scratch — I&apos;ll guide you step by step.</p>
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'migrate' && (
          <>
            <WizHeader title="Bring your project into DrawReady" back="q1" setStep={setStep} onClose={onClose} />
            <div className="p-6"><MigrationFlow /></div>
          </>
        )}

        {step === 'q2' && (
          <>
            <WizHeader title="A couple of quick questions" back="q1" setStep={setStep} onClose={onClose} />
            <div className="p-6 space-y-5">
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-2">Has the project been funded yet?</p>
                <div className="flex gap-2">
                  {[['yes', 'Yes, funded'], ['no', 'Not yet']].map(([k, l]) => (
                    <button key={k} onClick={() => setFunded(k)} className={`px-4 py-2 rounded-xl border text-sm font-semibold ${funded === k ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 mb-2">Have contractors been selected?</p>
                <div className="flex gap-2">
                  {[['yes', 'Yes, chosen'], ['no', 'Not yet']].map(([k, l]) => (
                    <button key={k} onClick={() => setHasContractors(k)} className={`px-4 py-2 rounded-xl border text-sm font-semibold ${hasContractors === k ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`}>{l}</button>
                  ))}
                </div>
              </div>
              <button
                disabled={!funded || !hasContractors}
                onClick={() => setStep('result')}
                className="w-full py-3 rounded-xl text-white font-bold disabled:opacity-40"
                style={{ background: NAVY }}
                data-testid="wiz-q2-continue"
              >
                Continue <ArrowRight className="w-4 h-4 inline ml-1" />
              </button>
            </div>
          </>
        )}

        {step === 'result' && (
          <>
            <WizHeader title={funded === 'yes' && hasContractors === 'yes' ? 'Jump straight to setup' : "Let's line up your contractors first"} back="q2" setStep={setStep} onClose={onClose} />
            <div className="p-6">
              {funded === 'yes' && hasContractors === 'yes' ? (
                <div className="space-y-3">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
                    <Rocket className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-emerald-900">Great — you&apos;re funded with contractors chosen. We&apos;ll skip bidding and drop you into <b>guided setup</b> to add loan terms + build your draw schedule.</p>
                  </div>
                  <div className="space-y-1.5">
                    {[['Add loan details', true], ['Confirm contractors', true], ['Build draw schedule', false, true], ['Request first draw', false]].map(([l, d, a], i) => (
                      <div key={i} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${a ? 'bg-blue-50 border border-blue-200' : ''}`}>
                        {d ? <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} /> : a ? <Clock className="w-4 h-4 text-blue-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                        <span className={`text-sm ${d ? 'text-slate-400 line-through' : a ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{l}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">We&apos;ll take you to <b>Project Hub</b> to run bidding, then convert to a live project once funded.</p>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center gap-2 overflow-x-auto pb-1">
                      {hubSteps.map(([Ic, lbl], i) => (
                        <React.Fragment key={lbl}>
                          <div className="flex flex-col items-center gap-1 min-w-[80px]">
                            <div className="w-9 h-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center"><Ic className="w-4 h-4 text-slate-600" /></div>
                            <span className="text-[10px] text-slate-600 text-center leading-tight">{lbl}</span>
                          </div>
                          {i < hubSteps.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <button className="w-full py-3 rounded-xl text-white font-bold" style={{ background: GREEN }}>Go to Project Hub <ArrowRight className="w-4 h-4 inline ml-1" /></button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------- main dashboard
export default function CentralDashboardPreview() {
  const [view, setView] = useState('active');
  const [wizard, setWizard] = useState(false);
  const list = view === 'active' ? ACTIVE : COMPLETED;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="flex items-end gap-[2px]"><div className="w-2 h-6 rounded-sm" style={{ background: GREEN }} /><div className="w-2 h-6 rounded-sm -ml-[2px]" style={{ background: GREEN }} /></div>
            <span className="font-extrabold tracking-tight" style={{ color: NAVY }}>DrawReady</span>
            <span className="text-xs text-slate-400 hidden sm:inline">· Central dashboard preview</span>
          </div>
          <div className="flex items-center gap-3">
            <button className="relative text-slate-400 hover:text-slate-700"><Bell className="w-5 h-5" /><span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ background: GREEN }}>3</span></button>
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: NAVY }}>JD</div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Toggle + primary actions */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1" data-testid="ac-toggle">
            {[['active', `Active (${ACTIVE.length})`], ['completed', `Completed (${COMPLETED.length})`]].map(([k, lbl]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`toggle-${k}`} className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${view === k ? 'text-white shadow' : 'text-slate-500 hover:text-slate-800'}`} style={view === k ? { background: NAVY } : {}}>{lbl}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setWizard(true)} className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-slate-50" data-testid="import-existing-btn">
              <ArrowRightLeft className="w-4 h-4" /> Import existing project
            </button>
            <button onClick={() => setWizard(true)} className="inline-flex items-center gap-2 text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:shadow-lg transition" style={{ background: GREEN }} data-testid="start-project-btn">
              <Plus className="w-4 h-4" /> Start a project
            </button>
          </div>
        </div>

        {/* Your next step hero */}
        <div className="rounded-3xl p-6 sm:p-7 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY}, #12508f)` }}>
          <div className="absolute -right-8 -top-10 w-44 h-44 rounded-full opacity-20" style={{ background: GREEN }} />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: '#a8d977' }}>Your next step</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">Request Maple Ridge draw #5</h2>
              <p className="text-blue-100 mt-2 text-sm max-w-lg">You&apos;re 41% drawn with $202K available. 3 phases are complete and bank-ready — submit to keep the project moving.</p>
            </div>
            <button className="inline-flex items-center gap-2 bg-white text-sm font-bold px-5 py-3 rounded-xl hover:shadow-lg transition" style={{ color: NAVY }}>Request draw <ArrowRight className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-5">
            {/* Getting started tracker */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-bold text-slate-800 flex items-center gap-2"><ListChecks className="w-4 h-4" style={{ color: GREEN }} /> Finish setting up Ellis Row</p>
                <span className="text-xs font-semibold text-slate-500">40% done</span>
              </div>
              <div className="space-y-1.5">
                {[['Create project', true], ['Add loan details', true], ['Invite contractors', false, true], ['Build draw schedule', false], ['Request first draw', false]].map(([lbl, done, active], i) => (
                  <div key={i} className={`flex items-center gap-3 rounded-lg px-3 py-2 ${active ? 'bg-blue-50 border border-blue-200' : ''}`}>
                    {done ? <CheckCircle2 className="w-4 h-4" style={{ color: GREEN }} /> : active ? <Clock className="w-4 h-4 text-blue-600" /> : <Circle className="w-4 h-4 text-slate-300" />}
                    <span className={`text-sm flex-1 ${done ? 'text-slate-400 line-through' : active ? 'font-bold text-slate-900' : 'text-slate-500'}`}>{lbl}</span>
                    {active && <button className="text-xs font-bold text-white px-3 py-1 rounded-lg" style={{ background: NAVY }}>Continue</button>}
                  </div>
                ))}
              </div>
            </div>

            {/* Projects list */}
            <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-slate-700 flex items-center gap-2 capitalize">
                <Building2 className="w-4 h-4 text-slate-400" /> {view} projects
              </div>
              {list.map((p) => (
                <div key={p.id} className="px-4 py-3 flex items-center gap-3 border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate flex items-center gap-2">
                      {p.name}
                      {p.setup && <span className="text-[9px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">setup</span>}
                      {p.gaps > 0 && <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">{p.gaps} W-9 missing</span>}
                    </p>
                    <p className="text-[11px] text-slate-400">{p.addr}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 w-40">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: `${p.drawn}%`, background: GREEN }} /></div>
                    <span className="text-[11px] text-slate-400 w-8 text-right">{p.drawn}%</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            <div className="rounded-2xl p-5 text-white" style={{ background: NAVY }}>
              <p className="text-[11px] uppercase tracking-wide font-bold text-blue-200">Portfolio</p>
              <p className="text-3xl font-extrabold mt-1">$386K</p>
              <p className="text-xs text-blue-200">available to draw</p>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Total loans</span><span className="font-semibold">$553K</span></div>
                <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" /> Daily interest</span><span className="font-semibold">$49.52</span></div>
                <div className="flex justify-between"><span className="text-blue-200 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Bank-ready</span><span className="font-semibold">80 / 100</span></div>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: GREEN }} /> Needs attention</p>
              {[
                [AlertTriangle, 'amber', '2 contractor W-9s missing', 'Oakwood Infill'],
                [DollarSign, 'blue', 'Draw #5 ready to submit', 'Maple Ridge'],
                [FileText, 'slate', 'Finish loan setup', 'Ellis Row'],
              ].map(([Ic, tone, t, s], i) => {
                const tones = { amber: 'text-amber-600', blue: 'text-blue-600', slate: 'text-slate-500' };
                return (
                  <div key={i} className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0">
                    <Ic className={`w-4 h-4 ${tones[tone]} flex-shrink-0`} />
                    <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-slate-800">{t}</p><p className="text-[10px] text-slate-400">{s}</p></div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                  </div>
                );
              })}
            </div>
            <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center">
              <Shield className="w-5 h-5 text-slate-400 mx-auto mb-1" />
              <p className="text-xs text-slate-500">Compliance <b className="text-slate-700">1 of 3 clear</b></p>
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 pb-6">
          Interactive preview · sample data. Click <b>Start a project</b> or <b>Import existing project</b> to walk the flow.
        </p>
      </div>

      {wizard && <StartProjectWizard onClose={() => setWizard(false)} />}
    </div>
  );
}
